// src/pages/profile-settings/doctor/PracticeTeamWelcome.jsx
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { RefreshCw } from "lucide-react";

import { callFunctionRaw, resolveFunctionAccessToken } from "@/lib/callFunction.js";
import SaveChip from "@/sot/primitives/SaveChip.jsx";
import TopBar from "@/sot/primitives/TopBar.jsx";
import AvatarThumb44 from "@/sot/primitives/AvatarThumb44.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import {
  PRACTICE_TEAM_CAPABILITY_STATES,
  getPracticeTeamCapabilityAreas,
  getPracticeTeamMemberIdentity,
  getPracticeTeamNavigation,
  getPracticeTeamRoleLabel,
  getPracticeTeamTasks,
} from "./practiceTeamWorkspaceModel.js";
import PracticeTeamWorkspaceHeader from "./PracticeTeamWorkspaceHeader.jsx";
import { usePracticeTeamMemberAccount } from "./practiceTeamMemberAccess.js";

function readMemberId(search = "") {
  try {
    return String(new URLSearchParams(search || "").get("member") || "").trim();
  } catch {
    return "";
  }
}

function isFrenchLanguageCode(value = "") {
  return String(value || "").toLowerCase().startsWith("fr");
}

function readCachedWelcomeInvite(userId = "") {
  try {
    if (typeof window === "undefined") return null;
    const parsed = JSON.parse(window.sessionStorage?.getItem("afrimed:practice-team:welcome-invite") || "null");
    if (!parsed?.invite?.id) return null;
    if (userId && parsed.user_id && parsed.user_id !== userId) return null;
    return parsed.invite;
  } catch {
    return null;
  }
}

function StatusPill({ children }) {
  return (
    <span className="inline-flex min-h-5 items-center gap-1 rounded-full bg-[rgba(0,189,196,0.12)] px-2 py-0.5 text-label-medium font-medium leading-4 text-[#008A8F] whitespace-nowrap">
      <span className="h-2 w-2 shrink-0 rounded-full bg-[#008A8F]" aria-hidden="true" />
      {children}
    </span>
  );
}

async function loadWelcome(memberId) {
  const accessToken = await resolveFunctionAccessToken({
    refreshIfMissing: true,
    forceRefresh: false,
  });
  if (!String(accessToken || "").trim()) {
    throw new Error("auth_required");
  }

  return callFunctionRaw(
    "practice-team-invite-lifecycle",
    {
      action: "welcome",
      member_id: memberId,
    },
    {
      accessToken,
      timeoutMs: 10000,
      preferFreshSession: false,
    },
  );
}

function cacheWelcomeInvite(userId, invite) {
  if (!userId || !invite?.id || typeof window === "undefined") return;
  try {
    window.sessionStorage?.setItem(
      "afrimed:practice-team:welcome-invite",
      JSON.stringify({
        user_id: userId,
        invite,
        cached_at: Date.now(),
      }),
    );
  } catch {
    // The authenticated resolver remains the source of truth; this only
    // preserves the already verified invite while account navigation mounts.
  }
}

export default function PracticeTeamWelcome() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth() || {};
  const { t, languageCode } = useLanguage();
  const isFrench = isFrenchLanguageCode(languageCode);
  const memberId = React.useMemo(() => readMemberId(location.search), [location.search]);
  const userId = auth?.user?.id || "";
  const authInvite = auth?.profile?.practice_team_invite || null;
  const cachedInvite = React.useMemo(() => readCachedWelcomeInvite(userId), [userId]);
  const resolvedInvite = authInvite || cachedInvite || null;
  const [invite, setInvite] = React.useState(() => resolvedInvite || null);
  const [loading, setLoading] = React.useState(() => !resolvedInvite);
  const [refreshing, setRefreshing] = React.useState(false);
  const [refreshMessage, setRefreshMessage] = React.useState("");
  const [error, setError] = React.useState("");
  const baseIdentity = React.useMemo(
    () => getPracticeTeamMemberIdentity(invite || {}, auth?.user || {}),
    [invite, auth?.user]
  );
  const memberEmail = String(baseIdentity?.email || "").trim().toLowerCase();
  const authenticatedEmail = String(auth?.user?.email || "").trim().toLowerCase();
  const authenticatedMember = memberEmail && authenticatedEmail && memberEmail === authenticatedEmail
    ? auth?.user
    : null;
  const authenticatedProfileAvatar = authenticatedMember
    ? String(auth?.profile?.avatar_url || auth?.profile?.profile_image_url || "").trim()
    : "";
  const { photoUrl, refresh: refreshMemberPhoto } = usePracticeTeamMemberAccount({
    user: authenticatedMember,
    identity: baseIdentity,
  });
  const [failedAvatarUrl, setFailedAvatarUrl] = React.useState("");
  // This row follows the actual delegated member's account only when the
  // authenticated session matches that member. It never reads an unrelated
  // Pharmacy/founder profile as a visual fallback.
  const identity = React.useMemo(
    () => ({
      ...baseIdentity,
      avatarUrl:
        photoUrl ||
        (baseIdentity.avatarUrl === failedAvatarUrl ? "" : baseIdentity.avatarUrl) ||
        (authenticatedProfileAvatar === failedAvatarUrl ? "" : authenticatedProfileAvatar),
    }),
    [authenticatedProfileAvatar, baseIdentity, failedAvatarUrl, photoUrl],
  );
  const retryMemberPhoto = React.useCallback(() => {
    if (identity?.avatarUrl) setFailedAvatarUrl(identity.avatarUrl);
    refreshMemberPhoto?.();
  }, [identity?.avatarUrl, refreshMemberPhoto]);
  const workspaceNavigation = React.useMemo(
    () => getPracticeTeamNavigation(invite?.role),
    [invite?.role]
  );
  const workspaceTasks = React.useMemo(
    () => getPracticeTeamTasks(invite?.role),
    [invite?.role]
  );
  const restrictedAreas = React.useMemo(
    () =>
      getPracticeTeamCapabilityAreas(invite?.role).filter(
        (area) => area.state !== PRACTICE_TEAM_CAPABILITY_STATES.AVAILABLE
      ),
    [invite?.role]
  );
  const refreshWorkspace = React.useCallback(async () => {
    if (refreshing || loading) return;
    setRefreshing(true);
    setError("");
    setRefreshMessage("");
    try {
      await auth?.refreshProfile?.();
      const targetMemberId = memberId || invite?.id || resolvedInvite?.id || "";
      const result = await loadWelcome(targetMemberId);
      const nextInvite = result?.invite || null;
      setInvite(nextInvite);
      cacheWelcomeInvite(userId, nextInvite);
      setRefreshMessage(t("settings.practiceTeam.workspace.refreshed", isFrench ? "Espace actualisé." : "Workspace refreshed."));
    } catch {
      setError(
        t(
          "settings.practiceTeam.welcome.error",
          isFrench
            ? "Nous n’avons pas pu charger la page d’accueil de votre équipe de pratique pour le moment."
            : "We could not load your Practice Team welcome page right now."
        )
      );
    } finally {
      setRefreshing(false);
    }
  }, [auth, invite?.id, isFrench, loading, memberId, refreshing, resolvedInvite?.id, t, userId]);
  const workspaceTitle = t(
    "settings.practiceTeam.workspace.title",
    isFrench ? "Espace Équipe de pratique" : "Practice Team workspace"
  );
  const workspaceSubtitle = t(
    "settings.practiceTeam.workspace.subtitle",
    isFrench ? "Accès délégué limité et sécurisé" : "Limited and secure delegated access"
  );
  const renderRefreshControl = () => (
    <button
      type="button"
      onClick={refreshWorkspace}
      disabled={loading || refreshing}
      className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-[12px] bg-[var(--state-primary-12)] px-4 text-label-large text-primary transition-colors hover:bg-[var(--state-primary-16)] disabled:opacity-60"
    >
      <RefreshCw className={`h-4 w-4 ${loading || refreshing ? "animate-spin" : ""}`} aria-hidden />
      {t("settings.practiceTeam.workspace.refreshShort", isFrench ? "Actualiser" : "Refresh")}
    </button>
  );
  React.useEffect(() => {
    // A cross-tab Supabase auth change can replace the authenticated user
    // while this route remains mounted. Never retain the former member's
    // delegated workspace content under the new identity.
    setInvite(resolvedInvite || null);
    setError("");
    setLoading(!resolvedInvite?.id);
  }, [userId, resolvedInvite]);

  React.useEffect(() => {
    let alive = true;

    async function run() {
      if (resolvedInvite?.id && !memberId) {
        setLoading(false);
        setError("");
        return;
      }
      if (!invite) setLoading(true);
      setError("");
      try {
        const result = await loadWelcome(memberId);
        if (!alive) return;
        const nextInvite = result?.invite || null;
        setInvite(nextInvite);
        cacheWelcomeInvite(userId, nextInvite);
      } catch {
        if (!alive) return;
        setError(
          t(
            "settings.practiceTeam.welcome.error",
            isFrench
              ? "Nous n’avons pas pu charger la page d’accueil de votre équipe de pratique pour le moment."
              : "We could not load your Practice Team welcome page right now."
          )
        );
      } finally {
        if (alive) setLoading(false);
      }
    }

    run();

    return () => {
      alive = false;
    };
  }, [memberId, isFrench, resolvedInvite?.id]);

  return (
    <div className="min-h-dvh bg-[rgb(var(--afrimed-shell-background))] text-[rgb(var(--clr-on-surface))]">
      <PracticeTeamWorkspaceHeader section="home" invite={invite} identity={identity} />
      <main className="afrimed-rail afrimed-rail--comfortable [--rail-pad:12px] pt-4 pb-12 lg:[--rail-pad:var(--afrimed-rail-gutter-desktop)]">
        <div className="md:hidden">
          <header className="w-full mb-4 md:mb-5">
            <div style={{ height: "env(safe-area-inset-top)" }} />
            <div className="flex min-h-16 items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <h1 className="truncate text-title-large text-on-surface">
                  {workspaceTitle}
                </h1>
                <p className="mt-1 truncate text-body-medium text-on-surface-var">
                  {workspaceSubtitle}
                </p>
              </div>
              <div className="shrink-0">{renderRefreshControl()}</div>
            </div>
          </header>
        </div>
        <div className="hidden md:block">
          <TopBar
            title={workspaceTitle}
            subtitle={workspaceSubtitle}
            titleClassName="text-headline-small text-on-surface truncate"
            trailingAlign="title"
            trailing={renderRefreshControl()}
          />
        </div>

        <div className="space-y-8">
          <section className="max-w-[46rem]">
            <h2 className="text-title-large text-on-surface">
              {t(
                "settings.practiceTeam.workspace.heading",
                isFrench
                  ? "Votre accès à l’équipe de pratique est actif, mais volontairement limité."
                  : "Your Practice Team access is active, but intentionally limited."
              )}
            </h2>
            <p className="mt-2 max-w-[46rem] text-body-medium text-on-surface-var">
              {t(
                "settings.practiceTeam.workspace.body",
                isFrench
                  ? "AfriMed™ prépare les workflows délégués de manière progressive. Pour l’instant, cet espace confirme votre rattachement, votre rôle et les zones sensibles qui restent verrouillées."
                  : "AfriMed™ is introducing delegated workflows progressively. For now, this workspace confirms your assignment, role and the sensitive areas that remain locked."
                )}
            </p>

            {error ? (
              <p className="mt-4 text-body-medium text-[rgb(var(--afrimed-danger))]">{error}</p>
            ) : null}
            {refreshing || refreshMessage ? (
              <p className="mt-4 text-body-medium text-on-surface-var" aria-live="polite" data-practice-team-refresh-state>
                {refreshing
                  ? t("settings.practiceTeam.workspace.refreshing", isFrench ? "Actualisation de l’accès..." : "Refreshing workspace access...")
                  : refreshMessage}
              </p>
            ) : null}
          </section>

          <section className="space-y-7" data-practice-team-workspace>
            <div className="space-y-3">
              <div className="hidden border-b border-[var(--Schemes-Outline-Variant,#E6E7EB)] py-3 text-body-medium text-on-surface-var md:grid md:grid-cols-[minmax(15rem,1.1fr)_minmax(13rem,1fr)_minmax(9rem,0.72fr)_minmax(10rem,0.72fr)] md:gap-4">
                <span>{t("settings.practiceTeam.table.name", isFrench ? "Nom" : "Name")}</span>
                <span>{t("settings.practiceTeam.workspace.practice", isFrench ? "Pratique • Médecin" : "Practice • Doctor")}</span>
                <span>{t("settings.practiceTeam.workspace.role", isFrench ? "Rôle attribué" : "Role assigned")}</span>
                <span>{t("settings.practiceTeam.workspace.status", isFrench ? "Statut" : "Status")}</span>
              </div>

              {invite ? (
                <div className="border-b border-[var(--Schemes-Outline-Variant,#E6E7EB)] py-3">
                  <div className="grid gap-3 md:grid-cols-[minmax(15rem,1.1fr)_minmax(13rem,1fr)_minmax(9rem,0.72fr)_minmax(10rem,0.72fr)] md:items-center md:gap-4">
                    <div className="flex min-w-0 items-start gap-3 md:items-center">
                      <span className="relative flex h-11 w-11 shrink-0 items-center justify-center">
                        <AvatarThumb44
                          src={identity.avatarUrl}
                          alt={identity.avatarUrl ? identity.displayName : ""}
                          shape="circle"
                          fallbackSrc=""
                          className="bg-[rgba(29,79,215,0.10)]"
                          imgClassName={identity.avatarUrl ? "" : "opacity-0"}
                          onImageError={retryMemberPhoto}
                        />
                        {!identity.avatarUrl ? (
                          <span className="absolute inset-0 flex items-center justify-center rounded-full text-label-large font-medium text-[rgb(var(--afrimed-primary))]">
                            {identity.initials}
                          </span>
                        ) : null}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-body-large text-on-surface">
                          {identity.displayName}
                        </p>
                        <div className="mt-0.5 space-y-0.5 md:hidden">
                          <p className="truncate text-body-small text-on-surface-var">
                            {identity.email}
                          </p>
                          <p className="truncate text-body-small text-on-surface-var">
                            {identity.practiceName}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="hidden min-w-0 text-body-medium text-on-surface-var md:block">
                      <span className="truncate">{identity.practiceName}</span>
                    </div>

                    <div className="min-w-0">
                      <span className="text-label-small text-on-surface-var md:hidden">
                        {t("settings.practiceTeam.workspace.role", isFrench ? "Rôle attribué" : "Role assigned")}
                      </span>
                      <div className="mt-1 flex items-center justify-between gap-3 md:mt-0 md:block">
                        <span
                          className="inline-flex h-8 max-w-full items-center rounded-full border border-[var(--Schemes-Outline-Variant,#E6E7EB)] bg-[rgb(var(--clr-surface))] px-3 text-label-medium font-medium text-on-surface-var"
                          aria-readonly="true"
                        >
                          <span className="truncate whitespace-nowrap">
                            {invite.role_label || getPracticeTeamRoleLabel(invite.role)}
                          </span>
                        </span>
                        <span className="shrink-0 md:hidden">
                          <StatusPill>
                            {t("settings.practiceTeam.workspace.activeLimited", isFrench ? "Actif - accès limité" : "Active - limited access")}
                          </StatusPill>
                        </span>
                      </div>
                    </div>

                    <div className="hidden min-w-0 md:block">
                      <StatusPill>
                        {t("settings.practiceTeam.workspace.activeLimited", isFrench ? "Actif - accès limité" : "Active - limited access")}
                      </StatusPill>
                    </div>
                  </div>
                </div>
              ) : loading ? (
                <div className="border-b border-[var(--Schemes-Outline-Variant,#E6E7EB)] py-3">
                  <p className="text-body-medium text-on-surface-var">
                    {t("common.loading", isFrench ? "Chargement..." : "Loading...")}
                  </p>
                </div>
              ) : null}
            </div>

            {invite ? (
              <>
                <section className="space-y-3">
                  <h2 className="text-title-small text-on-surface">
                    {t("settings.practiceTeam.workspace.mapTitle", isFrench ? "Navigation du workspace" : "Workspace map")}
                  </h2>
                  <p className="max-w-[48rem] text-body-medium text-on-surface-var">
                    {t(
                      "settings.practiceTeam.workspace.mapBody",
                      isFrench
                        ? "Les sections du membre sont dérivées des permissions du rôle. Les routes non activées restent fermées jusqu’aux prochains slices."
                        : "Member sections are derived from role permissions. Routes that are not enabled remain closed until later slices."
                    )}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {workspaceNavigation.filter((section) => section.id !== "home").map((section) => {
                      const isImplementedRoute =
                        (
                          section.id === "tasks" ||
                          section.id === "appointments" ||
                          section.id === "messages" ||
                          section.id === "patients"
                        ) &&
                        section.enabled;

                      return (
                        <SaveChip
                          key={section.id}
                          label={section.label}
                          onClick={isImplementedRoute ? () => navigate(section.path) : undefined}
                          tabIndex={isImplementedRoute ? undefined : -1}
                          aria-disabled={isImplementedRoute ? undefined : "true"}
                          className={[
                            "min-h-8 px-3 py-1 text-label-large",
                            isImplementedRoute ? "" : "pointer-events-none",
                            section.enabled
                              ? ""
                              : "!bg-[rgba(86,106,139,0.10)] !text-on-surface-var opacity-70",
                          ].join(" ")}
                        />
                      );
                    })}
                  </div>
                </section>

                <section className="max-w-[44rem] space-y-3">
                  <h2 className="text-title-small text-on-surface">
                    {t("settings.practiceTeam.workspace.allowedTitle", isFrench ? "Actions disponibles maintenant" : "Available now")}
                  </h2>
                  <ul className="space-y-3">
                    {workspaceTasks.map((item) => (
                      <li key={item} className="flex gap-3 text-body-medium text-on-surface-var">
                        <span className="mt-[0.55rem] h-2 w-2 shrink-0 rounded-full bg-[rgb(var(--afrimed-primary))]" aria-hidden="true" />
                        <p className="text-body-large text-on-surface-var">{item}</p>
                      </li>
                    ))}
                  </ul>
                </section>

                <section className="px-1 pt-2">
                  <h2 className="text-title-medium text-on-surface">
                    {t("settings.practiceTeam.workspace.lockedTitle", isFrench ? "Zones verrouillées" : "Locked areas")}
                  </h2>
                  <p className="mt-2 max-w-[48rem] text-body-medium text-on-surface-var">
                    {t(
                      "settings.practiceTeam.workspace.lockedBody",
                      isFrench
                        ? "Ces zones restent fermées tant que les autorisations, la vérification et l’audit de production ne sont pas finalisés."
                        : "These areas remain closed until production permissions, verification and auditing are complete."
                    )}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {restrictedAreas.map((area) => (
                      <SaveChip
                        key={area.id}
                        label={area.label}
                        tabIndex={-1}
                        aria-disabled="true"
                        className="pointer-events-none min-h-8 px-3 py-1 text-label-large"
                      />
                    ))}
                  </div>
                </section>
              </>
            ) : null}
          </section>
        </div>
      </main>
    </div>
  );
}
